import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/shared/lib/supabase'
import type { ProjectVersion, ProjectVersionScopingData } from '@/shared/types'

interface CreateVersionParams {
  projectId: string
  currentVersion: number
  previousScopingData?: ProjectVersionScopingData
}

interface CreateVersionResult {
  version: ProjectVersion
  newVersionNumber: number
}

/**
 * Create a new version of a project's scoping
 * - Creates new entry in project_versions
 * - Copies scoping_data from previous version (with reference)
 * - Updates current_version on the project
 */
async function createNewVersion({
  projectId,
  currentVersion,
  previousScopingData,
}: CreateVersionParams): Promise<CreateVersionResult> {
  const newVersionNumber = currentVersion + 1

  // Prepare scoping_data for new version
  // Copy sections but reset their status, keep reference to previous version
  // Preserve conversation_id so NicolAI can resume with context
  const newScopingData: ProjectVersionScopingData = {
    copied_from_version: currentVersion,
    conversation_id: previousScopingData?.conversation_id,
    progress_percent: 0,
    sections: previousScopingData?.sections
      ? Object.fromEntries(
          Object.entries(previousScopingData.sections).map(([key, section]) => [
            key,
            {
              status: 'pending' as const,
              content: section.content, // Keep content for reference
            },
          ])
        )
      : undefined,
  }

  // Create new version entry
  const { data: version, error: versionError } = await supabase
    .from('project_versions')
    .insert({
      project_id: projectId,
      version_number: newVersionNumber,
      status: 'scoping',
      scoping_data: newScopingData,
    })
    .select()
    .single()

  if (versionError) {
    console.error('Failed to create version:', versionError)
    throw new Error(versionError.message)
  }

  // Update project: increment version, reset to scoping status
  const { error: projectError } = await supabase
    .from('projects')
    .update({
      current_version: newVersionNumber,
      status: 'scoping',
      scoping_completed_at: null,
      specs_pdf_url: null,
    })
    .eq('id', projectId)

  if (projectError) {
    // Rollback: delete the created version
    await supabase.from('project_versions').delete().eq('id', version.id)
    console.error('Failed to update project:', projectError)
    throw new Error(projectError.message)
  }

  return {
    version: version as ProjectVersion,
    newVersionNumber,
  }
}

export function useCreateVersion() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: createNewVersion,
    onSuccess: () => {
      // Invalidate all project and team/dashboard queries
      queryClient.invalidateQueries({ queryKey: ['project'] })
      queryClient.invalidateQueries({ queryKey: ['teams'] })
    },
  })
}
